import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning } from '../../components/LessonUI';

const HTMLHead = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Document Architecture</div>
                <h1>The &lt;head&gt; Section Mastery</h1>
                <p className="hero-lead">
                    The &lt;head&gt; section is the invisible control center of your web page. Master meta tags,
                    SEO optimization, and resource loading for professional web development.
                </p>
            </div>

            <div className="content-section">
                <h2>What is the &lt;head&gt; Section?</h2>
                <p>
                    The <code>&lt;head&gt;</code> element contains <strong>metadata</strong> about the HTML document.
                    Unlike the <code>&lt;body&gt;</code>, content in the head is not displayed on the pageit provides
                    information <em>about</em> the page to browsers, search engines, and other web services.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Head Structure</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>My Web Page</title>
    <link rel="stylesheet" href="styles.css">
    <script src="script.js" defer></script>
</head>
<body>
    <!-- Visible content goes here --&gt;
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Required Elements</h2>

                <div className="concept-card highlight">
                    <h3>1. Character Encoding (&lt;meta charset&gt;)</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<meta charset="UTF-8">`} />
                    </div>
                    <p>
                        Declares the character encoding for the document. <strong>UTF-8</strong> supports all languages
                        and special characters. This should be the <em>first</em> element in the head.
                    </p>
                    <Warning><strong> Critical:</strong> Place this before any content that contains special characters,
                        including the <code>&lt;title&gt;</code>.</Warning>
                </div>

                <div className="concept-card highlight">
                    <h3>2. Page Title (&lt;title&gt;)</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<title>Learn HTML - Complete Tutorial | MyWebsite</title>`} />
                    </div>
                    <p>
                        The page title appears in:
                    </p>
                    <ul>
                        <li>Browser tabs and window titles</li>
                        <li>Search engine results (SERPs)</li>
                        <li>Browser bookmarks</li>
                        <li>Social media share previews</li>
                    </ul>
                    <div className="tip-box">
                        <strong> SEO Best Practices:</strong>
                        <ul>
                            <li>Keep it under 60 characters (search engines truncate longer titles)</li>
                            <li>Include primary keyword near the beginning</li>
                            <li>Make it unique for each page</li>
                            <li>Use a consistent format: "Page Title | Site Name"</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Viewport Meta Tag (Mobile Essential)</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Responsive Design Foundation</span>
                    </div>
                    <CodeBlock language="html" code={`<meta name="viewport" content="width=device-width, initial-scale=1.0">`} />
                </div>

                <p>
                    This meta tag is <strong>crucial for responsive design</strong>. Without it, mobile browsers
                    will render your page at desktop width (typically 980px) and scale it down, making text tiny.
                </p>

                <div className="comparison-table">
                    <h3>Viewport Attributes:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Attribute</th>
                                <th>Value</th>
                                <th>Purpose</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>width</code></td>
                                <td><code>device-width</code></td>
                                <td>Match screen width</td>
                            </tr>
                            <tr>
                                <td><code>initial-scale</code></td>
                                <td><code>1.0</code></td>
                                <td>Initial zoom level (1 = 100%)</td>
                            </tr>
                            <tr>
                                <td><code>maximum-scale</code></td>
                                <td><code>5.0</code></td>
                                <td>Max zoom (avoid disabling!)</td>
                            </tr>
                            <tr>
                                <td><code>user-scalable</code></td>
                                <td><code>yes</code></td>
                                <td>Allow pinch-to-zoom (accessibility!)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <Warning><strong> Accessibility Warning:</strong> Never use <code>user-scalable=no</code> or <code>maximum-scale=1.0</code>.
                    This prevents users from zooming, violating accessibility guidelines (WCAG).</Warning>
            </div>

            <div className="content-section">
                <h2>SEO Meta Tags</h2>

                <div className="code-example">
                    <div className="code-header">
                        <span>Essential SEO Metadata</span>
                    </div>
                    <CodeBlock language="html" code={`<meta name="description" content="Learn HTML5 from scratch. Complete tutorial covering syntax, semantics, forms, and modern best practices.">
<meta name="keywords" content="HTML tutorial, HTML5, web development, learn HTML">
<meta name="author" content="Your Name">
<meta name="robots" content="index, follow">`} />
                </div>

                <div className="explanation-list">
                    <h3>Meta Tag Breakdown:</h3>
                    <ul>
                        <li>
                            <strong>description:</strong> Appears in search results below the title.
                            Keep it 150-160 characters. Make it compellingit's your "ad copy" in SERPs.
                        </li>
                        <li>
                            <strong>keywords:</strong> Largely ignored by modern search engines (Google stopped using it in 2009),
                            but some smaller engines may still use it.
                        </li>
                        <li>
                            <strong>robots:</strong> Instructions for search engine crawlers:
                            <ul>
                                <li><code>index</code> = include in search results</li>
                                <li><code>noindex</code> = don't include</li>
                                <li><code>follow</code> = crawl links on this page</li>
                                <li><code>nofollow</code> = don't crawl links</li>
                            </ul>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Linking External Resources</h2>

                <h3>Stylesheets (CSS)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- External stylesheet --&gt;
<link rel="stylesheet" href="styles.css">

<!-- Multiple stylesheets (order matters!) --&gt;
<link rel="stylesheet" href="reset.css">
<link rel="stylesheet" href="main.css">
<link rel="stylesheet" href="responsive.css">

<!-- Media-specific styles --&gt;
<link rel="stylesheet" href="print.css" media="print">
<link rel="stylesheet" href="screen.css" media="screen">`} />
                </div>

                <h3>Favicon</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Standard favicon --&gt;
<link rel="icon" type="image/x-icon" href="/favicon.ico">

<!-- Modern PNG favicon (better quality) --&gt;
<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">

<!-- Apple Touch Icon (iOS home screen) --&gt;
<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">`} />
                </div>

                <h3>Preconnect & Prefetch (Performance)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Preconnect to external domains (faster resource loading) --&gt;
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://cdn.example.com">

<!-- DNS prefetch (lighter version) --&gt;
<link rel="dns-prefetch" href="https://analytics.google.com">

<!-- Prefetch resources for next page --&gt;
<link rel="prefetch" href="next-page.html">
<link rel="prefetch" href="large-image.jpg">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>JavaScript in the Head</h2>
                <p>
                    JavaScript can be loaded in the <code>&lt;head&gt;</code>, but it blocks HTML parsing by default.
                    Use <code>defer</code> or <code>async</code> to avoid performance issues.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Script Loading Strategies</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- BLOCKING: Executes immediately, blocks parsing (bad for performance) --&gt;
<script src="script.js"></script>

<!-- DEFER: Downloads in parallel, executes after HTML parsing (best for most cases) --&gt;
<script src="script.js" defer></script>

<!-- ASYNC: Downloads and executes immediately when ready (good for independent scripts) --&gt;
<script src="analytics.js" async></script>

<!-- Inline script (use sparingly) --&gt;
<script>
    console.log('Page loaded');
</script>`} />
                </div>

                <div className="comparison-table">
                    <h3>Script Loading Comparison:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Method</th>
                                <th>Parsing</th>
                                <th>Execution Order</th>
                                <th>Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Default</td>
                                <td>Blocks</td>
                                <td>Immediate</td>
                                <td> Avoid (slow)</td>
                            </tr>
                            <tr>
                                <td><code>defer</code></td>
                                <td>Doesn't block</td>
                                <td>After HTML</td>
                                <td>Main scripts</td>
                            </tr>
                            <tr>
                                <td><code>async</code></td>
                                <td>Doesn't block</td>
                                <td>ASAP (random)</td>
                                <td>Independent scripts</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Open Graph & Social Media</h2>
                <p>
                    Control how your page appears when shared on social media (Facebook, Twitter, LinkedIn, etc.).
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Open Graph Protocol (Facebook, LinkedIn)</span>
                    </div>
                    <CodeBlock language="html" code={`<meta property="og:title" content="Learn HTML5 - Complete Tutorial">
<meta property="og:description" content="Master HTML5 from basics to advanced techniques.">
<meta property="og:image" content="https://example.com/preview-image.jpg">
<meta property="og:url" content="https://example.com/html-tutorial">
<meta property="og:type" content="website">`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Twitter Cards</span>
                    </div>
                    <CodeBlock language="html" code={`<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:title" content="Learn HTML5 - Complete Tutorial">
<meta name="twitter:description" content="Master HTML5 from basics to advanced techniques.">
<meta name="twitter:image" content="https://example.com/preview-image.jpg">
<meta name="twitter:creator" content="@yourusername">`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Professional Head Example</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Production-Ready Head Section</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <!-- Character encoding (must be first) --&gt;
    <meta charset="UTF-8">
    
    <!-- Viewport for responsive design --&gt;
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    
    <!-- Page title --&gt;
    <title>Learn HTML5 - Complete Tutorial | WebDev Mastery</title>
    
    <!-- SEO Meta Tags --&gt;
    <meta name="description" content="Master HTML5 from scratch. Complete tutorial covering syntax, semantics, forms, and modern best practices.">
    <meta name="keywords" content="HTML tutorial, HTML5, web development">
    <meta name="author" content="WebDev Mastery">
    <meta name="robots" content="index, follow">
    
    <!-- Open Graph (Facebook, LinkedIn) --&gt;
    <meta property="og:title" content="Learn HTML5 - Complete Tutorial">
    <meta property="og:description" content="Master HTML5 from basics to advanced techniques.">
    <meta property="og:image" content="https://example.com/preview.jpg">
    <meta property="og:url" content="https://example.com/html-tutorial">
    <meta property="og:type" content="website">
    
    <!-- Twitter Card --&gt;
    <meta name="twitter:card" content="summary_large_image">
    <meta name="twitter:title" content="Learn HTML5 - Complete Tutorial">
    <meta name="twitter:description" content="Master HTML5 from basics to advanced techniques.">
    <meta name="twitter:image" content="https://example.com/preview.jpg">
    
    <!-- Favicon --&gt;
    <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
    <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">
    
    <!-- Preconnect to external domains --&gt;
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    
    <!-- Stylesheets --&gt;
    <link rel="stylesheet" href="css/reset.css">
    <link rel="stylesheet" href="css/main.css">
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap">
    
    <!-- Scripts --&gt;
    <script src="js/app.js" defer></script>
    <script src="https://analytics.example.com/script.js" async></script>
    
    <!-- Canonical URL (prevents duplicate content issues) --&gt;
    <link rel="canonical" href="https://example.com/html-tutorial">
</head>
<body>
    <!-- Page content --&gt;
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Put <code>&lt;meta charset&gt;</code> first</li>
                            <li>Include viewport meta tag for mobile</li>
                            <li>Write compelling, unique titles (50-60 chars)</li>
                            <li>Write concise descriptions (150-160 chars)</li>
                            <li>Use <code>defer</code> for scripts</li>
                            <li>Include Open Graph tags</li>
                            <li>Add favicons for all devices</li>
                            <li>Use preconnect for external resources</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Disable zoom (<code>user-scalable=no</code>)</li>
                            <li>Use blocking scripts</li>
                            <li>Duplicate titles across pages</li>
                            <li>Stuff keywords unnaturally</li>
                            <li>Use <code>noindex</code> on important pages</li>
                            <li>Load too many stylesheets (combine them)</li>
                            <li>Forget the charset declaration</li>
                            <li>Use deprecated meta tags</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLHead;



